import React from 'react';
import {
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import { LinkRouter } from '../header/BreadcrumbNav';
import { useAuth, useAccount } from '../../auth';
import { useLocation } from 'react-router-dom';
import { routes } from '../../app/routes';
import { colors, mainNavStyles } from '../../utils/theme';
import { getNavGroups } from '../../utils/routes-utls';
import { useAccountEntitlements, hasFeature } from '../../data/useEntitlements';
import { MainNavProps, NavGroups } from '../../types/navigation.types';

const MainNav: React.FC<MainNavProps> = ({ layoutState }) => {
  const { user: authUser } = useAuth();
  const { currentAccount } = useAccount();
  const location = useLocation();
  const pathnames = location.pathname.split('/').filter((x) => x);

  const accountRole = (authUser as any)?.roles?.find(
    (r: any) => r.account.id === currentAccount?.id,
  )?.role;

  const entitlementsQuery = useAccountEntitlements(currentAccount?.id);
  const entitlements = entitlementsQuery.data?.getData() as any[] | undefined;

  return Object.entries(getNavGroups(routes))
    .filter(([navGroup]) => {
      // Only show "Account" nav group for account-admin users
      if (navGroup === 'Account' && accountRole !== 'account-admin') {
        return false;
      }
      return true;
    })
    .map(([navGroup, { navItems }]) => {
      const visibleItems = navItems.filter(({ name }) => {
        // Hide Canvas if account doesn't have the feature entitlement
        if (name === 'canvases' && !hasFeature(entitlements, 'canvas')) {
          return false;
        }
        return true;
      });

      // Hide the entire nav group if no items are visible
      if (visibleItems.length === 0) {
        return null;
      }

      return (
        <List
          key={navGroup}
          sx={{ overflow: 'hidden' }}
          subheader={
            layoutState == 'open' ? (
              <Typography
                variant="overline"
                color="textSecondary"
                sx={{
                  paddingLeft: 3,
                  paddingTop: 2,
                  display: 'block',
                  fontSize: '10px',
                }}
              >
                {navGroup}
              </Typography>
            ) : (
              <Divider />
            )
          }
        >
          {visibleItems.map(({ title, icon, name }, index) => (
            <ListItem
              key={title}
              title={title}
              disablePadding
              sx={{
                display: 'block',
                ...(pathnames.includes(name) && {
                  ...mainNavStyles.navItem.active,
                }),
              }}
            >
              <LinkRouter
                underline="none"
                to={`/${currentAccount.name}/${name}`}
                variant="inherit"
                color="textPrimary"
              >
                <ListItemButton
                  sx={[
                    {
                      minHeight: 48,
                      px: 2.5,
                    },
                    layoutState == 'open'
                      ? {
                          justifyContent: 'initial',
                        }
                      : {
                          justifyContent: 'center',
                        },
                  ]}
                >
                  <ListItemIcon
                    sx={[
                      {
                        minWidth: 0,
                        justifyContent: 'center',
                      },
                      layoutState == 'open'
                        ? {
                            mr: 3,
                          }
                        : {
                            mr: 'auto',
                          },
                    ]}
                  >
                    {React.createElement(icon, {
                      style: {
                        ...(pathnames.includes(name) && {
                          color: colors.common.black,
                        }),
                      },
                    })}
                  </ListItemIcon>
                  <ListItemText
                    primary={title}
                    sx={[
                      layoutState == 'open'
                        ? {
                            ...(pathnames.includes(name) && {
                              color: colors.primary.dark,
                            }),
                            opacity: 1,
                          }
                        : {
                            opacity: 0,
                          },
                    ]}
                  />
                </ListItemButton>
              </LinkRouter>
            </ListItem>
          ))}
        </List>
      );
    })
    .filter(Boolean);
};
export default MainNav;
